import { useMemo, useState } from "react";

import { useCustomerSms, type CustomerSmsMessage } from "../queries/useCustomerSms";
import { Badge } from "./ui/badge";
import { Card, CardContent } from "./ui/card";

type DirectionFilter = "all" | "in" | "out";

function formatDateTime(value?: string | null) {
  if (!value) return "No date";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone: "America/New_York",
    timeZoneName: "short",
  }).format(date);
}

function sourceLabel(source: CustomerSmsMessage["source"]) {
  if (source === "twilio_inbound") return "Twilio inbound";
  if (source === "twilio_outbound") return "Twilio outbound";
  return "Local outbound";
}

function statusVariant(message: CustomerSmsMessage) {
  if (message.error_code || message.status === "failed" || message.status === "undelivered") return "red";
  if (message.direction === "in") return "green";
  return "slate";
}

function isInDateRange(message: CustomerSmsMessage, from: string, to: string) {
  const ts = new Date(message.date).getTime();
  if (Number.isNaN(ts)) return true;
  if (from) {
    const start = new Date(`${from}T00:00:00`).getTime();
    if (ts < start) return false;
  }
  if (to) {
    const end = new Date(`${to}T23:59:59`).getTime();
    if (ts > end) return false;
  }
  return true;
}

export function Customer360SmsTab({ contactId, active }: { contactId: number; active: boolean }) {
  const { data, isLoading, isError } = useCustomerSms(contactId, active);
  const [direction, setDirection] = useState<DirectionFilter>("all");
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (data?.messages || []).filter((message) => {
      if (direction !== "all" && message.direction !== direction) return false;
      if (!isInDateRange(message, fromDate, toDate)) return false;
      if (!needle) return true;
      return [
        message.body,
        message.status,
        message.from,
        message.to,
        message.quote_id,
        message.template_id,
      ].some((value) => String(value || "").toLowerCase().includes(needle));
    });
  }, [data?.messages, direction, fromDate, search, toDate]);

  if (isLoading) {
    return <Card className="p-6 text-sm text-slate-500">Loading SMS thread...</Card>;
  }

  if (isError || !data) {
    return (
      <Card className="p-6">
        <div className="text-sm font-medium text-slate-950">SMS thread unavailable</div>
        <p className="mt-1 text-sm text-slate-500">Check the Customer SMS endpoint and auth token.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {data.opted_out_phones.length > 0 ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <div className="font-semibold">STOP active on SMS</div>
          <div className="mt-1">
            {data.opted_out_phones.map((entry) => entry.phone).join(", ")}
          </div>
        </div>
      ) : null}

      <Card>
        <CardContent className="space-y-4">
          <div className="grid gap-3 md:grid-cols-4">
            <SmsStat label="Total" value={String(data.stats.total)} />
            <SmsStat label="Inbound" value={String(data.stats.inbound)} />
            <SmsStat label="Outbound" value={String(data.stats.outbound)} />
            <SmsStat label="Last SMS" value={formatDateTime(data.stats.last_message_at)} />
          </div>

          <div className="grid gap-3 md:grid-cols-[160px_1fr_140px_140px]">
            <select
              className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm"
              value={direction}
              onChange={(event) => setDirection(event.target.value as DirectionFilter)}
            >
              <option value="all">All directions</option>
              <option value="in">Inbound only</option>
              <option value="out">Outbound only</option>
            </select>
            <input
              className="h-9 rounded-md border border-slate-200 px-3 text-sm"
              placeholder="Search body, phone, status, quote..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <input
              className="h-9 rounded-md border border-slate-200 px-3 text-sm"
              type="date"
              value={fromDate}
              onChange={(event) => setFromDate(event.target.value)}
              aria-label="From date"
            />
            <input
              className="h-9 rounded-md border border-slate-200 px-3 text-sm"
              type="date"
              value={toDate}
              onChange={(event) => setToDate(event.target.value)}
              aria-label="To date"
            />
          </div>

          <div className="text-xs text-slate-500">
            Phones: {data.phones.length ? data.phones.join(", ") : "No phone on file"}
          </div>
        </CardContent>
      </Card>

      {filtered.length === 0 ? (
        <Card className="p-6 text-sm text-slate-500">
          No SMS messages match this view.
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((message) => (
            <SmsMessageRow key={message.id} message={message} />
          ))}
        </div>
      )}
    </div>
  );
}

function SmsStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-slate-100 bg-slate-50 px-3 py-2">
      <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">{label}</div>
      <div className="mt-1 text-sm font-semibold text-slate-950">{value}</div>
    </div>
  );
}

function SmsMessageRow({ message }: { message: CustomerSmsMessage }) {
  const outbound = message.direction === "out";
  const inbound = message.direction === "in";
  return (
    <Card className={outbound ? "border-blue-100 bg-blue-50/40" : "border-emerald-100 bg-emerald-50/40"}>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={outbound ? "default" : "green"}>{outbound ? "Outbound" : "Inbound"}</Badge>
            <Badge variant={statusVariant(message)}>{message.status || "unknown"}</Badge>
            {message.is_stop || message.is_opted_out_phone ? <Badge variant="red">STOP</Badge> : null}
            {message.template_id ? <Badge variant="slate">{message.template_id}</Badge> : null}
          </div>
          <div className="text-xs text-slate-500">{formatDateTime(message.date)}</div>
        </div>

        <div className={`whitespace-pre-wrap break-words text-sm leading-6 text-slate-950 ${inbound ? "italic" : ""}`}>
          {message.body || <span className="text-slate-400">Empty message body</span>}
        </div>

        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
          <span>{sourceLabel(message.source)}</span>
          <span>From {message.from || "unknown"}</span>
          <span>To {message.to || "unknown"}</span>
          {message.quote_id ? <span>Quote #{message.quote_id}</span> : null}
          {message.message_sid ? <span>SID {message.message_sid}</span> : null}
          {message.error_message ? <span className="text-red-700">{message.error_message}</span> : null}
        </div>
      </CardContent>
    </Card>
  );
}
